import axios from "axios";
import { getApiBaseUrl } from "@/config/api";
import type { CaseState, NotifyCase, NotifyCaseDetail, NotifyEvent, NotifyMe } from "./types";

/** Calls to `/api/notify`. They use the global axios, so AuthContext's
 * interceptor adds the Bearer token and refreshes it on a 401. */

const base = () => `${getApiBaseUrl()}/api/notify`;
const caseUrl = (siteId: string, studyUid: string) =>
  `${base()}/cases/${encodeURIComponent(siteId)}/${encodeURIComponent(studyUid)}`;

/** The backend's reason (`detail`) when it gave one, else the error's message. */
export function notifyErrorText(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    return typeof detail === "string" ? detail : error.message;
  }
  return error instanceof Error ? error.message : String(error);
}

export const errorStatus = (error: unknown): number | undefined =>
  axios.isAxiosError(error) ? error.response?.status : undefined;

/** The caller's active Notify sites and roles. */
export async function getMe(): Promise<NotifyMe> {
  return (await axios.get(`${base()}/me`)).data as NotifyMe;
}

/** A site's cases in one state, oldest flag first. */
export async function listCases(siteId: string, state: CaseState): Promise<NotifyCase[]> {
  const { data } = await axios.get(`${base()}/cases`, { params: { site_id: siteId, state } });
  return (data as { cases: NotifyCase[] }).cases;
}

/** One case and its events, in time order. */
export async function getCase(siteId: string, studyUid: string): Promise<NotifyCaseDetail> {
  return (await axios.get(caseUrl(siteId, studyUid))).data as NotifyCaseDetail;
}

export type AckResult =
  /** The caller owns the case: `created` false when they already did. */
  | { kind: "acked"; created: boolean; case: NotifyCase }
  /** Someone else acknowledged first. */
  | { kind: "taken"; ownerId: string; ownerName: string | null; at: string | null }
  /** 409 with no owner: the case changed under us, or is not ackable. */
  | { kind: "retry"; detail: string };

interface AckConflict {
  detail: string;
  owner_person_id: string | null;
  owner_display_name: string | null;
  acknowledged_at: string | null;
}

/** Acknowledge a case (radiologists): first ack wins. Other errors throw. */
export async function ackCase(siteId: string, studyUid: string): Promise<AckResult> {
  try {
    const { data } = await axios.post(`${caseUrl(siteId, studyUid)}/ack`);
    const { created, case: row } = data as { created: boolean; case: NotifyCase };
    return { kind: "acked", created, case: row };
  } catch (error) {
    if (!axios.isAxiosError(error) || error.response?.status !== 409) throw error;
    const conflict = error.response.data as AckConflict;
    if (!conflict.owner_person_id) return { kind: "retry", detail: conflict.detail };
    return {
      kind: "taken",
      ownerId: conflict.owner_person_id,
      ownerName: conflict.owner_display_name,
      at: conflict.acknowledged_at,
    };
  }
}

/** Record that the caller got the alert (any role). The first receipt per
 * person wins: a repeat returns that first one with `created` false. */
export async function postReceipt(
  siteId: string,
  studyUid: string,
): Promise<{ created: boolean; at: string }> {
  const { data } = await axios.post(`${caseUrl(siteId, studyUid)}/receipt`);
  const { created, event } = data as { created: boolean; event: NotifyEvent };
  return { created, at: event.at };
}

/** The VAPID public key (base64url) browsers subscribe with; 503 when push is off. */
export async function getVapidKey(): Promise<string> {
  const { data } = await axios.get(`${base()}/push/vapid-public-key`);
  return (data as { application_server_key: string }).application_server_key;
}

/** Store this browser's subscription (an upsert by endpoint); returns its `sub_id`. */
export async function saveSubscription(
  siteId: string,
  subscription: PushSubscriptionJSON,
): Promise<string> {
  const { data } = await axios.post(`${base()}/push/subscriptions`, {
    site_id: siteId,
    endpoint: subscription.endpoint,
    keys: subscription.keys,
  });
  return (data as { sub_id: string }).sub_id;
}

/** Remove one of the caller's subscriptions; a 404 (already gone) is not an error. */
export async function deleteSubscription(siteId: string, subId: string): Promise<void> {
  try {
    await axios.delete(`${base()}/push/subscriptions/${encodeURIComponent(subId)}`, {
      params: { site_id: siteId },
    });
  } catch (error) {
    if (errorStatus(error) !== 404) throw error;
  }
}
